import "./Site.scss";
import { useEffect, useRef, useState, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import debounce from "lodash/debounce";
import clsx from "clsx";

import {
  Box,
  OverflowContainer,
  Skeleton,
  Typography,
} from "@husqvarna/ui-core";
import { RootState } from "../../shared/store";
import { Husqvarnamap, HusqvarnamapHandle } from "husqvarnamap";
import { useSaveAreasMutation } from "../projectApi";
import SaveAreasModel from "../models/SaveAreasModel";
import { CuttingQuality } from "../models/Enums";
import {
  numberToCuttingQuality,
  getDefaultMapLocation,
  getMapDataFromProject,
  getMapEquipment,
  getMapAreaId,
  MAP_CONFIG,
} from "../../shared/utils";
import ToggleButton from "../../shared/components/ToggleButton";
import MowerDialog from "../components/Equipment/MowerDialog/MowerDialog";
import MowerList from "../components/Equipment/MowerList";
import NoSite from "../components/Equipment/NoSite";
import EquipmentDisclaimerToast from "../components/Equipment/EquipmentDisclaimerToast/EquipmentDisclaimerToast";
import { Product } from "../models/Product";
import { ProposalProduct } from "../models/ProposalProduct";

type Mode = "create-area" | "add-mower";

const getModeFromPath = (pathname: string): Mode => {
  return pathname.includes("add-mower") || pathname.includes("equipment")
    ? "add-mower"
    : "create-area";
};

const Site = () => {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  const project = useSelector(
    (state: RootState) => state.project.selectedProject
  );
  const [saveAreas] = useSaveAreasMutation();
  const [mapData, setMapData] = useState<ReturnType<typeof getMapDataFromProject>>();
  const mapRef = useRef<HusqvarnamapHandle>(null);
  
  const [mode, setMode] = useState<Mode>(() => getModeFromPath(location.pathname));

  const saveFunction = useCallback((model: SaveAreasModel) => {
    saveAreas(model)
      .unwrap()
      .catch((err) => {
        console.error("Save failed:", err);
      });
  }, [saveAreas]);

  // Equipment-specific state
  const [equipmentState, setEquipmentState] = useState({
    showNoSiteWarning: undefined as Boolean | undefined,
    isDrawerOpen: false,
    showLeasingToast: false,
    selectedMower: undefined as Product | undefined,
    selectedProposalProduct: undefined as ProposalProduct | undefined,
    scrollToTop: false,
    activeAccordions: ["assigned", "catalogue"] as string[],
  });
  const areaHover = useSelector((state: RootState) => state.areaHover);
  const overflowContainerRef = useRef<HTMLDivElement>(null);
  const isSavingRef = useRef(false);

  const debouncedSaveAreas = useCallback(
    debounce(() => {
      if (mapRef.current && mode === "create-area") {
        const currentState = mapRef.current.getCurrentState();
        if (currentState) {
          const saveData: SaveAreasModel = {
            areas: (currentState.areas ?? []).map((area) => ({
              id: area.id,
              size: area.size,
              name: area.name ?? "",
              notes: area.notes,
              coveragePercent: 0,
              complexityPercentage: area.complexityPercentage,
              proposalProductConnections: [],
              cuttingQuality: numberToCuttingQuality[area.cuttingQuality ?? 0] ?? CuttingQuality.Regular,
              maxSlope: area.maxSlope ?? 0,
              availableHoursToCutPerDay: area.availableHoursToCutPerDay ? Number(area.availableHoursToCutPerDay) : 0,
              polygonData: {
                type: area.polygonData.type ?? "Polygon",
                coordinates: area.polygonData.coordinates
              }
            })),
            projectId: project?.id ?? "",
            pointsOfInterest: currentState.pointsOfInterest ?? [],
          };
          saveFunction(saveData);
        }
      }
    }, 500),
    [saveFunction, project?.id, mode]
  );

  const equipmentSaveFunction = useCallback(() => {
    if (isSavingRef.current) return;
    isSavingRef.current = true;

    setEquipmentState((prev) => ({
      ...prev,
      scrollToTop: true,
      activeAccordions: prev.activeAccordions.includes("assigned")
        ? prev.activeAccordions
        : [...prev.activeAccordions, "assigned"],
    }));

    isSavingRef.current = false;
  }, []);

  const debouncedEquipmentSave = useCallback(
    debounce(() => {
      equipmentSaveFunction();
    }, 500),
    [equipmentSaveFunction]
  );

  const handleMapChange = useCallback(() => {
    if (mode === "create-area") {
      debouncedSaveAreas();
    } else {
      debouncedEquipmentSave();
    }
  }, [debouncedSaveAreas, debouncedEquipmentSave, mode]);

  useEffect(() => {
    const newMode = getModeFromPath(location.pathname);
    if (mode !== newMode) {
      setMode(newMode);
    }
  }, [location.pathname, mode]);

  useEffect(() => {
    if (project) {
      setEquipmentState((prev) => ({
        ...prev,
        showNoSiteWarning: !project.areaCount ? true : false,
      }));

      if (project.areas) {
        setMapData(getMapDataFromProject(project));
      }
    }
  }, [project]);

  const handleAreaSelect = useCallback((areaIds: string[]) => {
    mapRef.current?.selectArea(areaIds);
  }, []);

  useEffect(() => {
    if (project && mapRef.current && mode === "add-mower" && project.areas) {
      project.areas.forEach((area) => {
        mapRef.current?.updateAreaEquipment(getMapAreaId(area), getMapEquipment(area, project));
      });
    }
  }, [project, mode]);

  useEffect(() => {
    if (mode === "add-mower" && project?.areas) {
      const translatedAreaIds = (areaHover.areaIds || []).map((dbId) => {
        const area = project.areas.find(a => a.id === dbId);
        return area ? getMapAreaId(area) : dbId;
      });
      handleAreaSelect(translatedAreaIds);
    }
  }, [areaHover, mode, handleAreaSelect, project]);

  useEffect(() => {
    if (equipmentState.scrollToTop && overflowContainerRef.current) {
      overflowContainerRef.current.scrollTop = 0;
      setEquipmentState((prev) => ({ ...prev, scrollToTop: false }));
    }
  }, [equipmentState.scrollToTop]);

  useEffect(() => {
    return () => {
      debouncedSaveAreas.cancel();
      debouncedEquipmentSave.cancel();
    };
  }, [debouncedSaveAreas, debouncedEquipmentSave]);

  const closeDrawer = useCallback(() => {
    setEquipmentState((prev) => ({ ...prev, isDrawerOpen: false }));
    setTimeout(() => {
      setEquipmentState((prev) => ({
        ...prev,
        selectedMower: undefined,
        selectedProposalProduct: undefined,
      }));
    }, 300);
  }, []);

  // Close MowerDialog when map mode changes (editable/not editable)
  // Use a ref to track previous mode to only close when mode actually changes
  const prevModeRef = useRef<Mode>(mode);
  useEffect(() => {
    if (prevModeRef.current !== mode && equipmentState.isDrawerOpen) {
      closeDrawer();
    }
    prevModeRef.current = mode;
  }, [mode, closeDrawer]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleMowerClick = useCallback((
    mower: Product,
    proposalProduct?: ProposalProduct
  ) => {
    setEquipmentState((prev) => ({
      ...prev,
      selectedMower: mower,
      selectedProposalProduct: proposalProduct,
      isDrawerOpen: true,
    }));
  }, []);

  const handleModeChange = useCallback((newMode: Mode) => {
    setMode(newMode);
    if (projectId) {
      if (newMode === "add-mower") {
        navigate(`/projects/${projectId}/new-setup/add-mower`);
      } else {
        navigate(`/projects/${projectId}/new-setup/create-area`);
      }
    }
  }, [navigate, projectId]);

  const equipmentDetailsClasses = clsx("equipment-details", {
    active: equipmentState.isDrawerOpen,
  });

  const toggleButtonOptions = [
    {
      label: intl.formatMessage({
        id: "general.createArea",
        defaultMessage: "Create area",
      }),
      value: "create-area" as Mode,
    },
    {
      label: intl.formatMessage({
        id: "general.addMower",
        defaultMessage: "Add mower",
      }),
      value: "add-mower" as Mode,
    },
  ];

  return (
    <>
      <EquipmentDisclaimerToast
        show={mode === "add-mower" && equipmentState.showLeasingToast}
        onClose={() => setEquipmentState((prev) => ({ ...prev, showLeasingToast: false }))}
        onLinkClick={() => console.log("click!")}
      />
      {project && mapData && (
        <div className="husqvarna-map">
          <Husqvarnamap
            ref={mapRef}
            mapId={MAP_CONFIG.mapId}
            accessToken={MAP_CONFIG.accessToken}
            projectName={project.name}
            projectId={project.id}
            apiEndPoint={MAP_CONFIG.apiEndPoint}
            onSaved={() => {
              if (mode === "create-area") {
                debouncedSaveAreas();
              }
            }}
            savedJSON={mapData as object}
            showSaveButton={false}
            onChange={handleMapChange}
            editable={mode === "create-area"}
            defaultLocation={getDefaultMapLocation(project)}
          />
          <Box className="toggle-button-container">
            <ToggleButton
              options={toggleButtonOptions}
              value={mode}
              onChange={(value) => handleModeChange(value as Mode)}
            />
          </Box>
        </div>
      )}

      <Box 
        className={clsx("equipment", { 
          open: equipmentState.isDrawerOpen, 
          "equipment-hidden": mode !== "add-mower" 
        })} 
        br={6}
      >
          <Box
            className="equipment-list"
            flexDirection="column"
            container
            pb={4}
          >
            {equipmentState.showNoSiteWarning === true && (
              <NoSite onGoToSiteClick={() => handleModeChange("create-area")} />
            )}
            {equipmentState.showNoSiteWarning === false && (
              <>
                <Box mt={6}>
                  <Typography variant="Subtitle2" mb={4} mx={6}>
                    <FormattedMessage
                      id="projects.equipment.mowerList.mowersHeading"
                      defaultMessage="Mowers"
                      description="Heading for list of mowers in equipment"
                    />
                  </Typography>
                  <Typography variant="Microcopy" mb={5} mx={6}>
                    <FormattedMessage
                      id="projects.equipment.mowerList.mowersDescription"
                      defaultMessage="To assign a mower to one or several areas, click on the mower and then select the area(s)."
                      description="Description for list of mowers in equipment"
                    />
                  </Typography>
                </Box>
                {!project && (
                  <Box flexDirection="column" gap={3}>
                    <Skeleton height="40px" mx={6} br={4} />
                    <Skeleton height="40px" mx={6} br={4} />
                    <Skeleton height="40px" mx={6} br={4} />
                  </Box>
                )}
                {project && (
                  <Box container noOverflow flexDirection="column">
                    <OverflowContainer
                      ref={overflowContainerRef}
                      direction="vertical"
                      fadeOut="mask"
                      pl={6}
                      pr={4}
                      mr={2}
                    >
                      <MowerList
                        project={project}
                        proposalMowers={project.proposal.mowers}
                        expandedAccordions={equipmentState.activeAccordions}
                        onItemClick={handleMowerClick}
                      />
                    </OverflowContainer>
                  </Box>
                )}
              </>
            )}
          </Box>
          <Box className={equipmentDetailsClasses}>
            {project && equipmentState.selectedMower && (
              <MowerDialog
                mower={equipmentState.selectedMower}
                proposalProduct={equipmentState.selectedProposalProduct}
                project={project}
                onCancel={closeDrawer}
              />
            )}
          </Box>
        </Box>
    </>
  );
};
export default Site;
